import React, { useState, useEffect } from 'react';
import { primaryButtonSx, pagePaperSx } from '../../styles/adminPageStyles';
import PageHeader from '../../components/PageHeader';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  Chip,
  IconButton,
  Tooltip,
  Alert,
  CircularProgress,
  useTheme,
  useMediaQuery,
  Card,
  CardContent,
  CardActions,
  Divider,
} from '@mui/material';
import {
  Edit as EditIcon,
  AttachFile as AttachFileIcon,
  Image as ImageIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { format } from 'date-fns';
import { API_BASE_URL } from '../../config/api';

interface Incident {
  incident_id: string;
  title: string;
  description: string;
  severity: 'low' | 'medium' | 'high';
  status: 'reported' | 'under_investigation' | 'action_required' | 'resolved' | 'closed';
  created_at: string;
  updated_at: string;
  user_id: string;
  team_leader_id: string;
  location_id: string;
  location_name: string;
  user_first_name: string;
  user_last_name: string;
  team_leader_first_name: string;
  team_leader_last_name: string;
  attachments: Attachment[];
  updates: IncidentUpdate[];
  history?: IncidentHistoryEntry[];
}

interface Attachment {
  attachment_id: string;
  file_name: string;
  file_path: string;
  file_type: string;
  file_url: string;
  created_at: string;
}

interface IncidentUpdate {
  update_id: string;
  status?: string;
  status_change?: string;
  comment?: string;
  update_text?: string;
  created_at: string;
  user_id: string;
  first_name: string;
  last_name: string;
}

interface IncidentHistoryEntry {
  history_id: string;
  incident_id: string;
  user_id: string;
  action_type: string;
  description: string;
  old_value: string | null;
  new_value: string | null;
  created_at: string;
  first_name: string;
  last_name: string;
}

// Single source of truth for incident status - labels match mobile app (my-incidents, incident-details, index)
const INCIDENT_STATUS_OPTIONS: { value: string; label: string }[] = [
  { value: 'reported', label: 'Reported' },
  { value: 'under_investigation', label: 'Under Investigation' },
  { value: 'action_required', label: 'Action Required' },
  { value: 'resolved', label: 'Resolved' },
  { value: 'closed', label: 'Closed' },
];

interface Location {
  location_id: string;
  location_name: string;
}

interface TeamLeader {
  user_id: string;
  first_name: string;
  last_name: string;
}

const IncidentManagement: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [incidents, setIncidents] = useState<Incident[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [selectedIncident, setSelectedIncident] = useState<Incident | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [newStatus, setNewStatus] = useState('');
  const [newComment, setNewComment] = useState('');
  const [filterSeverity, setFilterSeverity] = useState<string>('');
  const [filterStatus, setFilterStatus] = useState<string>('');
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  
  // Create incident dialog state
  const [openCreateDialog, setOpenCreateDialog] = useState(false);
  const [createFormData, setCreateFormData] = useState({
    title: '',
    description: '',
    locationId: '',
    teamLeaderId: '',
    severity: 'low' as 'low' | 'medium' | 'high',
  });
  const [locations, setLocations] = useState<Location[]>([]);
  const [teamLeaders, setTeamLeaders] = useState<TeamLeader[]>([]);
  const [attachments, setAttachments] = useState<File[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [updateAttachments, setUpdateAttachments] = useState<File[]>([]);

  useEffect(() => {
    fetchIncidents();
  }, []);

  useEffect(() => {
    if (openCreateDialog) {
      setCreateError(null); // Clear any previous errors
      fetchLocationsAndTeamLeaders();
    }
  }, [openCreateDialog]);

  // Helper function to check if attachment is an image
  const isImageFile = (fileType: string | null | undefined, fileName: string | null | undefined) => {
    if (!fileType && !fileName) return false;
    const imageTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/heic', 'image/heif'];
    const imageExtensions = /\.(jpg|jpeg|png|gif|heic|heif)$/i;
    const typeCheck = fileType ? imageTypes.includes(fileType.toLowerCase()) : false;
    const nameCheck = fileName ? imageExtensions.test(fileName) : false;
    return typeCheck || nameCheck;
  };

  const fetchIncidents = async () => {
    try {
      setLoading(true);
      setError(null);
      console.log('🔍 Fetching incidents from:', `${API_BASE_URL}/incidents`);
      
      const response = await axios.get(`${API_BASE_URL}/incidents`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      console.log('📦 Raw response:', response);
      console.log('📦 Response data:', response.data);
      console.log('📦 Response data type:', typeof response.data);
      console.log('📦 Is array?', Array.isArray(response.data));
      
      // Handle response - backend returns array directly or wrapped
      let incidentsData = [];
      if (Array.isArray(response.data)) {
        incidentsData = response.data;
        console.log('✅ Found array directly, length:', incidentsData.length);
      } else if (response.data && Array.isArray(response.data.data)) {
        incidentsData = response.data.data;
        console.log('✅ Found array in data property, length:', incidentsData.length);
      } else if (response.data && response.data.success && Array.isArray(response.data.data)) {
        incidentsData = response.data.data;
        console.log('✅ Found array in success.data, length:', incidentsData.length);
      } else {
        console.warn('⚠️ Unexpected incidents response format:', response.data);
        console.warn('⚠️ Response structure:', JSON.stringify(response.data, null, 2));
        incidentsData = [];
      }
      
      // Normalize severity values to handle case differences
      const normalizedIncidents = incidentsData.map((incident: any) => ({
        ...incident,
        severity: incident.severity ? incident.severity.toLowerCase() : 'low',
        // Ensure attachments and updates are arrays
        attachments: Array.isArray(incident.attachments) ? incident.attachments : [],
        updates: Array.isArray(incident.updates) ? incident.updates : []
      }));
      
      console.log('✅ Fetched and normalized incidents:', normalizedIncidents.length);
      console.log('📋 Incidents data:', normalizedIncidents);
      setIncidents(normalizedIncidents);
      setError(null);
    } catch (err: any) {
      const errorMessage = err.response?.data?.error || err.response?.data?.message || err.message || 'Failed to fetch incidents';
      setError(errorMessage);
      console.error('❌ Error fetching incidents:', err);
      console.error('❌ Error response:', err.response?.data);
      console.error('❌ Error status:', err.response?.status);
      setIncidents([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchLocationsAndTeamLeaders = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      setCreateError('Authentication required. Please log in again.');
      return;
    }

    let locationsError = false;
    let teamLeadersError = false;
    let locationsLoaded = false;
    let teamLeadersLoaded = false;

    // Fetch locations
    try {
      const locationsRes = await axios.get(`${API_BASE_URL}/locations`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Handle locations response
      let locationData: Location[] = [];
      if (locationsRes.data && locationsRes.data.success && Array.isArray(locationsRes.data.data)) {
        locationData = locationsRes.data.data.map((item: any) => ({
          location_id: item.location_id || item.locationId,
          location_name: item.location_name || item.locationName
        }));
        locationsLoaded = true;
      } else if (Array.isArray(locationsRes.data)) {
        locationData = locationsRes.data.map((item: any) => ({
          location_id: item.location_id || item.locationId,
          location_name: item.location_name || item.locationName
        }));
        locationsLoaded = true;
      }
      
      if (locationsLoaded) {
        setLocations(locationData);
        console.log('Locations loaded successfully:', locationData.length);
      } else {
        console.warn('Locations response format unexpected:', locationsRes.data);
        setLocations([]);
      }
    } catch (err: any) {
      locationsError = true;
      console.error('Error fetching locations:', err.response?.data || err.message || err);
      setLocations([]);
    }

    // Fetch team leaders
    try {
      const teamLeadersRes = await axios.get(`${API_BASE_URL}/users/team-leaders`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Handle team leaders response
      if (teamLeadersRes.data && teamLeadersRes.data.success && Array.isArray(teamLeadersRes.data.data)) {
        setTeamLeaders(teamLeadersRes.data.data);
        teamLeadersLoaded = true;
      } else if (Array.isArray(teamLeadersRes.data)) {
        setTeamLeaders(teamLeadersRes.data);
        teamLeadersLoaded = true;
      } else {
        console.warn('Team leaders response format unexpected:', teamLeadersRes.data);
        setTeamLeaders([]);
      }
      
      if (teamLeadersLoaded) {
        console.log('Team leaders loaded successfully:', teamLeadersRes.data?.data?.length || teamLeadersRes.data?.length || 0);
      }
    } catch (err: any) {
      teamLeadersError = true;
      console.error('Error fetching team leaders:', err.response?.data || err.message || err);
      setTeamLeaders([]);
    }

    // Only show error if both failed or if locations failed (required field)
    if (locationsError && teamLeadersError) {
      setCreateError('Failed to load form data. Please check your connection and try again.');
    } else if (locationsError) {
      setCreateError('Failed to load locations (required). Please try again.');
    } else if (teamLeadersError && !locationsLoaded) {
      setCreateError('Failed to load form data. Please try again.');
    } else {
      // Clear error if at least locations loaded (required field)
      setCreateError(null);
    }
  };

  const handleViewIncident = async (incident: Incident) => {
    const status = (incident.status || 'reported').toLowerCase();
    const validStatus = INCIDENT_STATUS_OPTIONS.some((o) => o.value === status) ? status : 'reported';
    setNewStatus(validStatus);
    setNewComment('');
    setOpenDialog(true);
    setSelectedIncident(incident);
    try {
      const { data } = await axios.get(`${API_BASE_URL}/incidents/${incident.incident_id}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setSelectedIncident(data);
    } catch {
      // Keep list incident if fetch fails
    }
  };

  const handleUpdateIncident = async () => {
    if (!selectedIncident) return;

    try {
      await axios.post(`${API_BASE_URL}/incidents/${selectedIncident.incident_id}/updates`, {
        status: newStatus,
        comment: newComment,
      }, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      // Upload any attached files
      if (updateAttachments.length > 0) {
        for (const file of updateAttachments) {
          const formData = new FormData();
          formData.append('file', file);
          await axios.post(
            `${API_BASE_URL}/incidents/${selectedIncident.incident_id}/upload-attachment`,
            formData,
            {
              headers: {
                'Authorization': `Bearer ${localStorage.getItem('token')}`,
                'Content-Type': 'multipart/form-data',
              }
            }
          );
        }
      }

      // Refresh incidents list
      await fetchIncidents();
      setOpenDialog(false);
      setNewComment('');
      setUpdateAttachments([]);
    } catch (err: any) {
      const message = err.response?.data?.message || err.response?.data?.error || 'Failed to update incident';
      setError(message);
      console.error('Error updating incident:', err.response?.data || err);
    }
  };

  const handleUpdateFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files) {
      const newFiles = Array.from(event.target.files);
      const validFiles = newFiles.filter(file => {
        if (file.size > 10 * 1024 * 1024) {
          setError(`File ${file.name} is too large. Maximum size is 10MB.`);
          return false;
        }
        return true;
      });
      setUpdateAttachments(prev => [...prev, ...validFiles]);
      event.target.value = '';
    }
  };

  const handleRemoveUpdateAttachment = (index: number) => {
    setUpdateAttachments(prev => prev.filter((_, i) => i !== index));
  };

  const getSeverityColor = (severity: string) => {
    const severityLower = severity ? severity.toLowerCase() : 'low';
    switch (severityLower) {
      case 'critical':
      case 'high':
        return 'error';
      case 'medium':
        return 'warning';
      case 'low':
        return 'success';
      default:
        return 'default';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'reported':
        return 'error';
      case 'under_investigation':
        return 'warning';
      case 'action_required':
        return 'warning';
      case 'resolved':
        return 'success';
      case 'closed':
        return 'default';
      default:
        return 'default';
    }
  };

  // Helper function to get display-friendly status name (matches mobile app labels)
  const getStatusDisplayName = (status: string) => {
    const s = (status || '').toLowerCase();
    switch (s) {
      case 'reported':
        return 'Reported';
      case 'under_investigation':
        return 'Under Investigation';
      case 'action_required':
        return 'Action Required';
      case 'resolved':
        return 'Resolved';
      case 'closed':
        return 'Closed';
      default:
        return status ? status.charAt(0).toUpperCase() + status.slice(1) : status;
    }
  };

  const handleCreateIncident = async () => {
    if (!createFormData.title || !createFormData.description || !createFormData.locationId) {
      setCreateError('Please fill in all required fields');
      return;
    }

    setSubmitting(true);
    setCreateError(null);

    try {
      // Create the incident
      const response = await axios.post(
        `${API_BASE_URL}/incidents`,
        {
          title: createFormData.title,
          description: createFormData.description,
          locationId: createFormData.locationId,
          teamLeaderId: createFormData.teamLeaderId || undefined,
          severity: createFormData.severity,
        },
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json',
          }
        }
      );

      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to create incident');
      }

      const incidentId = response.data.data.incident_id;

      // Upload attachments if any
      if (attachments.length > 0) {
        for (const file of attachments) {
          const formData = new FormData();
          formData.append('file', file);

          await axios.post(
            `${API_BASE_URL}/incidents/${incidentId}/upload-attachment`,
            formData,
            {
              headers: {
                'Authorization': `Bearer ${localStorage.getItem('token')}`,
                'Content-Type': 'multipart/form-data',
              }
            }
          );
        }
      }

      console.log('✅ Incident created successfully, ID:', incidentId);
      
      // Reset form and close dialog
      setCreateFormData({
        title: '',
        description: '',
        locationId: '',
        teamLeaderId: '',
        severity: 'low',
      });
      setAttachments([]);
      setOpenCreateDialog(false);
      
      // Refresh incidents list with a small delay to ensure backend has processed
      console.log('🔄 Refreshing incidents list...');
      setTimeout(async () => {
        await fetchIncidents();
      }, 500);
    } catch (err: any) {
      setCreateError(err.response?.data?.message || 'Failed to create incident. Please try again.');
      console.error('Error creating incident:', err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files) {
      const newFiles = Array.from(event.target.files);
      // Check file size (10MB limit)
      const validFiles = newFiles.filter(file => {
        if (file.size > 10 * 1024 * 1024) {
          setCreateError(`File ${file.name} is too large. Maximum size is 10MB.`);
          return false;
        }
        return true;
      });
      setAttachments(prev => [...prev, ...validFiles]);
    }
  };

  const handleRemoveAttachment = (index: number) => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
  };

  const filteredIncidents = incidents.filter(incident => {
    // Normalize severity for comparison (backend uses lowercase, filter uses capitalized)
    const incidentSeverity = incident.severity ? incident.severity.toLowerCase() : 'low';
    const filterSeverityLower = filterSeverity ? filterSeverity.toLowerCase() : '';
    const matchesSeverity = !filterSeverity || incidentSeverity === filterSeverityLower;
    const matchesStatus = !filterStatus || incident.status === filterStatus;
    
    // Handle date filtering
    let matchesStartDate = true;
    let matchesEndDate = true;
    if (incident.created_at) {
      try {
        const incidentDate = new Date(incident.created_at);
        if (startDate) {
          const start = new Date(startDate);
          start.setHours(0, 0, 0, 0);
          matchesStartDate = incidentDate >= start;
        }
        if (endDate) {
          const end = new Date(endDate);
          end.setHours(23, 59, 59, 999);
          matchesEndDate = incidentDate <= end;
        }
      } catch (e) {
        console.warn('Error parsing incident date:', incident.created_at, e);
      }
    }
    
    return matchesSeverity && matchesStatus && matchesStartDate && matchesEndDate;
  });

  return (
    <Box sx={{ width: '100%', maxWidth: '100%', pt: 0.5, pb: 3 }}>
      <PageHeader
        title="Incidents"
        subtitle="Compliance"
        action={
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => setOpenCreateDialog(true)}
            sx={primaryButtonSx}
          >
            Create Incident
          </Button>
        }
      />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Paper sx={{ ...pagePaperSx, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth>
              <InputLabel>Severity</InputLabel>
              <Select
                value={filterSeverity}
                onChange={(e) => setFilterSeverity(e.target.value)}
                label="Severity"
                size={isMobile ? "small" : "medium"}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="low">Low</MenuItem>
                <MenuItem value="medium">Medium</MenuItem>
                <MenuItem value="high">High</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                label="Status"
                size={isMobile ? "small" : "medium"}
              >
                <MenuItem value="">All</MenuItem>
                {INCIDENT_STATUS_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              type="date"
              label="Start Date"
              InputLabelProps={{ shrink: true }}
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              size={isMobile ? "small" : "medium"}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              type="date"
              label="End Date"
              InputLabelProps={{ shrink: true }}
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              size={isMobile ? "small" : "medium"}
            />
          </Grid>
        </Grid>
      </Paper>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress />
        </Box>
      ) : isMobile ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {filteredIncidents
            .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
            .map((incident) => (
              <Card key={incident.incident_id}>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                    <Box>
                      <Typography variant="subtitle1" fontWeight="bold">
                        {incident.title}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Location: {incident.location_name || 'N/A'}
                      </Typography>
                    </Box>
                    <Box display="flex" gap={1}>
                      <Chip
                        label={incident.severity}
                        color={getSeverityColor(incident.severity) as any}
                        size="small"
                      />
                      <Chip
                        label={getStatusDisplayName(incident.status)}
                        color={getStatusColor(incident.status) as any}
                        size="small"
                      />
                    </Box>
                  </Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Created: {format(new Date(incident.created_at), 'MMM d, yyyy HH:mm')}
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    {incident.description}
                  </Typography>
                </CardContent>
                <Divider />
                <CardActions sx={{ justifyContent: 'space-between', p: 2, flexWrap: 'wrap', gap: 1 }}>
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    {incident.attachments.length > 0 && (
                      <Tooltip title={`${incident.attachments.length} attachments (${incident.attachments.filter(att => isImageFile(att.file_type, att.file_name)).length} images)`}>
                        <IconButton size="small">
                          {incident.attachments.some(att => isImageFile(att.file_type, att.file_name)) ? 
                            <ImageIcon /> : <AttachFileIcon />
                          }
                        </IconButton>
                      </Tooltip>
                    )}
                    {incident.updates.length > 0 && (
                      <Typography variant="caption" color="text.secondary">
                        {incident.updates.length} update{incident.updates.length !== 1 ? 's' : ''}
                      </Typography>
                    )}
                  </Box>
                  <Button
                    size="small"
                    startIcon={<EditIcon />}
                    onClick={() => handleViewIncident(incident)}
                  >
                    View details & history
                  </Button>
                </CardActions>
              </Card>
            ))}
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
            <TablePagination
              component="div"
              count={filteredIncidents.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={(_, newPage) => setPage(newPage)}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={[5, 10, 25]}
              labelRowsPerPage=""
              labelDisplayedRows={({ from, to, count }) => `${from}-${to} of ${count}`}
            />
          </Box>
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Title</TableCell>
                <TableCell>Severity</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Created</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredIncidents
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((incident) => (
                  <TableRow key={incident.incident_id}>
                    <TableCell>{incident.title}</TableCell>
                    <TableCell>
                      <Chip
                        label={incident.severity}
                        color={getSeverityColor(incident.severity) as any}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={getStatusDisplayName(incident.status)}
                        color={getStatusColor(incident.status) as any}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {format(new Date(incident.created_at), 'MMM d, yyyy HH:mm')}
                    </TableCell>
                    <TableCell>
                      <Tooltip title="View details & change history">
                        <IconButton
                          onClick={() => handleViewIncident(incident)}
                          color="primary"
                          size="small"
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      {incident.updates.length > 0 && (
                        <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                          {incident.updates.length} update{incident.updates.length !== 1 ? 's' : ''}
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={filteredIncidents.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
          />
        </TableContainer>
      )}

      <Dialog
        open={openDialog}
        onClose={() => { setOpenDialog(false); setUpdateAttachments([]); setNewComment(''); }}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>Incident Details</DialogTitle>
        <DialogContent>
          {selectedIncident && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="h6" gutterBottom>
                {selectedIncident.title}
              </Typography>
              <Typography color="textSecondary" gutterBottom>
                Created: {format(new Date(selectedIncident.created_at), 'MMM d, yyyy HH:mm')}
              </Typography>
              <Typography paragraph>
                {selectedIncident.description}
              </Typography>

              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" gutterBottom>
                  Attachments
                </Typography>
                {selectedIncident.attachments.length > 0 ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {selectedIncident.attachments.map((attachment) => (
                      <Box key={attachment.attachment_id}>
                        {isImageFile(attachment.file_type, attachment.file_name) ? (
                          <Box sx={{ mb: 1 }}>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                              {attachment.file_name}
                            </Typography>
                            <Box
                              component="img"
                              src={attachment.file_url}
                              alt={attachment.file_name}
                              sx={{
                                maxWidth: '100%',
                                maxHeight: isMobile ? '200px' : '300px',
                                width: 'auto',
                                height: 'auto',
                                borderRadius: 1,
                                border: '1px solid',
                                borderColor: 'divider',
                                cursor: 'pointer',
                                '&:hover': {
                                  opacity: 0.8
                                }
                              }}
                              onClick={() => window.open(attachment.file_url, '_blank')}
                              onError={(e) => {
                                console.error('Failed to load image:', attachment.file_url);
                                // Hide the image and show a fallback
                                const target = e.currentTarget as HTMLImageElement;
                                target.style.display = 'none';
                                const fallback = target.nextElementSibling as HTMLElement;
                                if (fallback) fallback.style.display = 'block';
                              }}
                            />
                            <Button
                              startIcon={<AttachFileIcon />}
                              href={attachment.file_url}
                              target="_blank"
                              size={isMobile ? "small" : "medium"}
                              variant="outlined"
                              sx={{ display: 'none' }}
                            >
                              {attachment.file_name} (Image failed to load)
                            </Button>
                          </Box>
                        ) : (
                          <Button
                            startIcon={<AttachFileIcon />}
                            href={attachment.file_url}
                            target="_blank"
                            size={isMobile ? "small" : "medium"}
                            variant="outlined"
                          >
                            {attachment.file_name}
                          </Button>
                        )}
                      </Box>
                    ))}
                  </Box>
                ) : (
                  <Typography color="textSecondary">No attachments</Typography>
                )}
              </Box>

              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" gutterBottom>
                  Updates
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                  History of changes to this incident.
                </Typography>
                {selectedIncident.history && selectedIncident.history.length > 0 ? (
                  selectedIncident.history.map((entry) => {
                    const userName = [entry.first_name, entry.last_name].filter(Boolean).join(' ') || 'Unknown';
                    return (
                      <Paper key={entry.history_id} variant="outlined" sx={{ p: 2, mb: 1.5 }}>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1, mb: 0.5 }}>
                          <Chip size="small" label={entry.action_type.replace('_', ' ')} variant="outlined" />
                          <Typography color="text.secondary" variant="caption">
                            {format(new Date(entry.created_at), 'MMM d, yyyy HH:mm')}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">· {userName}</Typography>
                        </Box>
                        <Typography variant="body2">{entry.description}</Typography>
                        {(entry.old_value || entry.new_value) && (
                          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                            {entry.old_value && getStatusDisplayName(entry.old_value)}
                            {entry.old_value && entry.new_value && ' → '}
                            {entry.new_value && getStatusDisplayName(entry.new_value)}
                          </Typography>
                        )}
                      </Paper>
                    );
                  })
                ) : selectedIncident.updates.length > 0 ? (
                  selectedIncident.updates.map((update) => {
                    const statusValue = update.status_change ?? update.status ?? '';
                    const commentText = update.update_text ?? update.comment ?? '';
                    const updaterName = [update.first_name, update.last_name].filter(Boolean).join(' ') || 'Unknown';
                    return (
                      <Paper key={update.update_id} variant="outlined" sx={{ p: 2, mb: 1.5 }}>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1, mb: commentText ? 1 : 0 }}>
                          <Chip
                            size="small"
                            label={getStatusDisplayName(statusValue)}
                            color={getStatusColor(statusValue) as any}
                          />
                          <Typography color="text.secondary" variant="caption">
                            {format(new Date(update.created_at), 'MMM d, yyyy HH:mm')}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            · {updaterName}
                          </Typography>
                        </Box>
                        {commentText && (
                          <Typography variant="body2" sx={{ mt: 1 }}>
                            {commentText}
                          </Typography>
                        )}
                      </Paper>
                    );
                  })
                ) : (
                  <Typography color="text.secondary">No updates yet.</Typography>
                )}
              </Box>

              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Update Status</InputLabel>
                <Select
                  value={newStatus}
                  onChange={(e) => setNewStatus(e.target.value)}
                  label="Update Status"
                  size={isMobile ? "small" : "medium"}
                >
                  {INCIDENT_STATUS_OPTIONS.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                fullWidth
                multiline
                rows={4}
                label="Add Comment"
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                size={isMobile ? "small" : "medium"}
                sx={{ mb: 2 }}
              />

              {/* Attach images / documents to the update */}
              <Box>
                <Typography variant="body2" fontWeight={500} gutterBottom>
                  Attach Images / Documents (optional)
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: updateAttachments.length > 0 ? 1.5 : 0 }}>
                  {updateAttachments.map((file, i) => (
                    <Chip
                      key={i}
                      label={file.name}
                      onDelete={() => handleRemoveUpdateAttachment(i)}
                      icon={
                        file.type.startsWith('image/') ? (
                          <ImageIcon fontSize="small" />
                        ) : (
                          <AttachFileIcon fontSize="small" />
                        )
                      }
                      size="small"
                      variant="outlined"
                    />
                  ))}
                </Box>
                <Button
                  component="label"
                  variant="outlined"
                  size="small"
                  startIcon={<AttachFileIcon />}
                >
                  Add Files
                  <input
                    type="file"
                    hidden
                    multiple
                    accept="image/*,.pdf,.doc,.docx,.txt"
                    onChange={handleUpdateFileSelect}
                  />
                </Button>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ borderTop: 1, borderColor: 'divider', p: 2 }}>
          <Button onClick={() => { setOpenDialog(false); setUpdateAttachments([]); setNewComment(''); }}>Cancel</Button>
          <Button
            onClick={handleUpdateIncident}
            variant="contained"
            color="primary"
            disabled={!newComment.trim()}
          >
            Update
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create Incident Dialog */}
      <Dialog
        open={openCreateDialog}
        onClose={() => {
          if (!submitting) {
            setOpenCreateDialog(false);
            setCreateFormData({
              title: '',
              description: '',
              locationId: '',
              teamLeaderId: '',
              severity: 'low',
            });
            setAttachments([]);
            setCreateError(null);
          }
        }}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>Create New Incident</DialogTitle>
        <DialogContent>
          {createError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {createError}
            </Alert>
          )}
          
          <Box sx={{ mt: 2 }}>
            <TextField
              fullWidth
              label="Title *"
              value={createFormData.title}
              onChange={(e) => setCreateFormData({ ...createFormData, title: e.target.value })}
              margin="normal"
              required
              size={isMobile ? "small" : "medium"}
            />

            <TextField
              fullWidth
              label="Description *"
              value={createFormData.description}
              onChange={(e) => setCreateFormData({ ...createFormData, description: e.target.value })}
              margin="normal"
              multiline
              rows={4}
              required
              size={isMobile ? "small" : "medium"}
            />

            <FormControl fullWidth margin="normal" required>
              <InputLabel>Location *</InputLabel>
              <Select
                value={createFormData.locationId}
                onChange={(e) => setCreateFormData({ ...createFormData, locationId: e.target.value })}
                label="Location *"
                size={isMobile ? "small" : "medium"}
              >
                {locations.map((location) => (
                  <MenuItem key={location.location_id} value={location.location_id}>
                    {location.location_name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth margin="normal">
              <InputLabel>Team Leader</InputLabel>
              <Select
                value={createFormData.teamLeaderId}
                onChange={(e) => setCreateFormData({ ...createFormData, teamLeaderId: e.target.value })}
                label="Team Leader"
                size={isMobile ? "small" : "medium"}
              >
                <MenuItem value="">None</MenuItem>
                {teamLeaders.map((leader) => (
                  <MenuItem key={leader.user_id} value={leader.user_id}>
                    {leader.first_name} {leader.last_name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth margin="normal">
              <InputLabel>Severity</InputLabel>
              <Select
                value={createFormData.severity}
                onChange={(e) => setCreateFormData({ ...createFormData, severity: e.target.value as 'low' | 'medium' | 'high' })}
                label="Severity"
                size={isMobile ? "small" : "medium"}
              >
                <MenuItem value="low">Low</MenuItem>
                <MenuItem value="medium">Medium</MenuItem>
                <MenuItem value="high">High</MenuItem>
              </Select>
            </FormControl>

            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Attachments (Optional)
              </Typography>
              <input
                accept="image/*,.pdf,.doc,.docx,.txt"
                style={{ display: 'none' }}
                id="file-upload"
                type="file"
                multiple
                onChange={handleFileSelect}
              />
              <label htmlFor="file-upload">
                <Button
                  variant="outlined"
                  component="span"
                  startIcon={<AttachFileIcon />}
                  size={isMobile ? "small" : "medium"}
                  sx={{ mb: 2 }}
                >
                  Add Files
                </Button>
              </label>
              
              {attachments.length > 0 && (
                <Box sx={{ mt: 1 }}>
                  {attachments.map((file, index) => (
                    <Box
                      key={index}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        p: 1,
                        mb: 1,
                        bgcolor: 'background.default',
                        borderRadius: 1,
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                        {file.type.startsWith('image/') ? (
                          <ImageIcon sx={{ mr: 1, color: 'text.secondary' }} />
                        ) : (
                          <AttachFileIcon sx={{ mr: 1, color: 'text.secondary' }} />
                        )}
                        <Typography variant="body2" sx={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {file.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                          {(file.size / 1024 / 1024).toFixed(2)} MB
                        </Typography>
                      </Box>
                      <IconButton
                        size="small"
                        onClick={() => handleRemoveAttachment(index)}
                        color="error"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  ))}
                </Box>
              )}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ borderTop: 1, borderColor: 'divider', p: 2 }}>
          <Button
            onClick={() => {
              setOpenCreateDialog(false);
              setCreateFormData({
                title: '',
                description: '',
                locationId: '',
                teamLeaderId: '',
                severity: 'low',
              });
              setAttachments([]);
              setCreateError(null);
            }}
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button
            onClick={handleCreateIncident}
            variant="contained"
            color="primary"
            disabled={submitting || !createFormData.title || !createFormData.description || !createFormData.locationId}
          >
            {submitting ? <CircularProgress size={20} /> : 'Create Incident'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default IncidentManagement; 